import moment from "moment-mini";
import {Engine} from "platform/engine/Engine";
import {
    ClearDealFormRanges,
    CloseDealsPayload,
    EditDealPayload,
    GoingEditDealPayload,
    GoingOpenDealPayload,
    OpenDealPayload,
    SetDealServerError,
    SetDealFormInProgress,
    UpdateDealForm, SetSymbolDetails, RollPositionsPayload
} from "core/redux/deal/DealReduxActions";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import {Deal} from "platform/protocol/trading/Deal";
import {TradeType} from "protocol/trade/TradeType";
import {SetTradingModal, ToggleMenu, ToggleMenuPayload} from "core/redux/app/AppReduxActions";
import TradingModalType from "enum/TradingModalType";
import {DealForm} from "core/redux/deal/DealReduxState";
import {XhrUtil} from "core/util/XhrUtil";
import {OpenDealRequest} from "protocol/trade/OpenDealRequest";
import {Quote} from "platform/protocol/trading/Quote";
import {PositionDirection as TradeDirection} from "platform/protocol/enum/PositionDirection";
import {AccountState} from "core/state/AccountState";
import {ServiceType} from "enum/ServiceType";
import {OpenDealResponse} from "protocol/trade/OpenDealResponse";
import {HttpReject} from "platform/network/http/Http";
import {BookEntryOrderRequest} from "protocol/trade/BookEntryOrderRequest";
import Utils from "platform/util/Utils";
import {AddInboxMessage} from "core/redux/inbox/InboxReduxActions";
import InboxMessageType from "enum/InboxMessageType";
import Parameter from "platform/util/Parameter";
import InboxParameter from "enum/InboxParameter";
import {ClosePosition} from "protocol/trade/ClosePosition";
import {BaseResponse} from "protocol/BaseResponse";
import {CancelEntryOrder} from "protocol/trade/CancelEntryOrder";
import {CloseMultiplePositions} from "protocol/trade/CloseMultiplePositions";
import {PromotionType} from "platform/protocol/enum/PromotionType";
import {CloseMultiplePositionsResponse, PositionThatFailedToClose} from "protocol/trade/CloseMultiplePositionsResponse";
import {HideLoader, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {OpenPositionExtended} from "protocol/trade/OpenPositionExtended";
import {EntryOrderExtended} from "protocol/trade/EntryOrderExtended";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import DateTimeFormat from "core/format/DateTimeFormat";
import {UpdatePositionRequest} from "protocol/trade/UpdatePositionRequest";
import {UpdateEntryOrderRequest} from "protocol/trade/UpdateEntryOrderRequest";
import DealEditTarget from "enum/DealEditTarget";
import {SymbolsState} from "core/state/SymbolsState";
import WebUtil from "platform/util/WebUtil";
import {GetSymbolDetailsRequest} from "protocol/symbol/GetSymbolDetailsRequest";
import {SymbolDetails} from "protocol/symbol/GetSymbolDetailsResponse";
import {TranslationKey} from "enum/TranslationKey";
import ChartEngine from "core/engine/ChartEngine";
import {TradeUtil} from "core/util/TradeUtil";
import Translations from "platform/translation/Translations";
import {TranslationParam} from "enum/TranslationParam";
import {FetchIsRollingContractsEnabled} from "core/redux/settings/SettingsReduxActions";
import {ClosePositionsByExposureResponse} from "protocol/trade/ClosePositionsByExposureResponse";
import {ClosePositionsByExposureRequest} from "protocol/trade/ClosePositionsByExposureRequest";
import {SetFilterVisible} from "core/redux/filter/FilterReduxActions";
import {GetSymbolExpirationDateRequest} from "protocol/symbol/GetSymbolExpirationDateRequest";
import {GetSymbolExpirationDateResponse} from "protocol/symbol/GetSymbolExpirationDateResponse";

export default class DealEngine extends Engine {

    private static _instance: DealEngine;

    public static instance(): DealEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
    }

    public onToggleMenu = (payload: ToggleMenuPayload) => {
        if (!payload.collapse) {
            const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
            symbolsState.openTradeSymbolId = null;
            setTimeout(() => {
                Platform.dispatch(ClearDealFormRanges({}));
            }, 150);
        }
    }

    public doGoingOpenDeal = async ({SymbolId, tradeType, Direction, stopLoss = null, takeProfit = null, comments = null, action}: GoingOpenDealPayload) => {
        const storeState: StoreState = Platform.reduxState<StoreState>();
        const {IsUserAllowedToTrade, ShouldShowInappropriatePopup} = storeState.account;
        if (IsUserAllowedToTrade) {
            const {form} = storeState.deal;
            if (Utils.isNull(form) || Utils.greaterThen0(form.PositionId) || form.SymbolId !== SymbolId || form.PositionDirection !== Direction
                    || form.StopLossValueRate !== stopLoss || form.TakeProfitValueRate !== takeProfit
                    || (Utils.isNotNull(tradeType) && form.RateRequestType !== tradeType) || form.Comments !== comments) {
                this._logger.debug("Going open deal for symbol id: " + SymbolId);
                const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
                symbolsState.openTradeSymbolId = SymbolId;
                const request: GetSymbolDetailsRequest = {SymbolId};
                const answer: [HttpReject, SymbolDetails] = await Utils.to(this.sendToAccountService(request, "GetSymbolDetails"));
                const Request: GetSymbolExpirationDateRequest = {symbolId: SymbolId};
                const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                const symbolDetails: SymbolDetails = answer[1] || {};
                Platform.dispatch(SetSymbolDetails({details: symbolDetails}));
                Platform.dispatch(ClearDealFormRanges({}));
                Platform.dispatch(UpdateDealForm({
                    form: {
                        SymbolId,
                        SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                        SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                        RateRequestType: tradeType || TradeType.MarketOrder,
                        PositionDirection: Direction,
                        Amount: symbolDetails.Amounts?.[0],
                        StopLossChecked: Utils.greaterThen0(stopLoss),
                        StopLossValueRate: Utils.greaterThen0(stopLoss) ? stopLoss : null,
                        TakeProfitChecked: Utils.greaterThen0(takeProfit),
                        TakeProfitValueRate: Utils.greaterThen0(takeProfit) ? takeProfit : null,
                        Comments: comments
                    }
                }));
                if (action) {
                    action();
                } else {
                    if (WebUtil.isMobile()) {
                        Platform.dispatch(SetTradingModal({
                            tradingModalType: TradingModalType.CreateEditTrade,
                            info: {
                                visible: true
                            }
                        }));
                    } else {
                        Platform.dispatch(SetFilterVisible({visible: false}));
                        Platform.dispatch(ToggleMenu({collapse: true}));
                    }
                }
            } else {
                this._logger.debug("Opening deal for symbol id: " + SymbolId + " already in progress");
            }
        } else {
            if (ShouldShowInappropriatePopup) {
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.KYC,
                    info: {visible: true}
                }));
            } else {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            trKey: TranslationKey.notAllowedToTradeError
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        }
    }

    public doGoingEditDeal = async (payload: GoingEditDealPayload) => {
        const {DealId, tradeType, dealEditTarget} = payload;
        const {form} = Platform.reduxState<StoreState>().deal;
        if (Utils.isNull(form) || Utils.isNull(form.PositionId) || form.PositionId !== DealId
                || (!form.StopLossChecked && dealEditTarget === DealEditTarget.StopLoss) || (!form.TakeProfitChecked && dealEditTarget === DealEditTarget.TakeProfit)) {
            this._logger.debug("Going update deal with id: " + DealId + ". Trade type: " + tradeType + " Edit target: " + dealEditTarget);
            Platform.dispatch(FetchIsRollingContractsEnabled({}));
            const isMarketOrder: boolean = tradeType === TradeType.MarketOrder;
            const path: string = isMarketOrder ? "GetExtendedPosition" : "GetExtendedEntryOrder";
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, any] = await Utils.to(this.sendToAccountService(DealId, path));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                XhrUtil.notifyReject(answer[0]);
            } else {
                Platform.dispatch(ClearDealFormRanges({}));
                if (isMarketOrder) {
                    const op: OpenPositionExtended = answer[1];
                    const Request: GetSymbolExpirationDateRequest = {symbolId: op.SymbolId};
                    const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                    Platform.dispatch(UpdateDealForm({
                        form: {
                            SymbolId: op.SymbolId,
                            SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                            SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                            Amount: op.Amount,
                            PositionDirection: op.PositionDirection,
                            PositionId: op.Id,
                            RateRequestType: TradeType.MarketOrder,
                            StopLossValueRate: Utils.greaterThen0(op.StopLossRate) ? op.StopLossRate : null,
                            StopLossValueAmount: Utils.isNotNull(op.StopLossAmount) ? op.StopLossAmount : null,
                            StopLossChecked: Utils.greaterThen0(op.StopLossRate) || dealEditTarget === DealEditTarget.StopLoss,
                            TakeProfitValueRate: Utils.greaterThen0(op.TakeProfitRate) ? op.TakeProfitRate : null,
                            TakeProfitValueAmount: Utils.isNotNull(op.TakeProfitAmount) ? op.TakeProfitAmount : null,
                            TakeProfitChecked: Utils.greaterThen0(op.TakeProfitRate) || dealEditTarget === DealEditTarget.TakeProfit,
                            HasUserEnteredStopLossAmount: op.HasUserEnteredStopLossAmount,
                            HasUserEnteredTakeProfitAmount: op.HasUserEnteredTakeProfitAmount
                        }
                    }));
                } else {
                    const eo: EntryOrderExtended = answer[1];
                    let ExpirationDay: number;
                    let ExpirationMonth: number;
                    let ExpirationYear: number;
                    if (Utils.greaterThen0(eo.ExpirationDate)) {
                        const expirationDate: moment.Moment = DateTimeFormat.Of(eo.ExpirationDate);
                        ExpirationDay = expirationDate.date();
                        ExpirationMonth = expirationDate.month() + 1;
                        ExpirationYear = expirationDate.year();
                    }
                    const Request: GetSymbolExpirationDateRequest = {symbolId: eo.SymbolId};
                    const Answer: [HttpReject, GetSymbolExpirationDateResponse] = await Utils.to(this.sendToWebProfitService(Request, "GetSymbolExpirationAndCloseOnlyDateTime"));
                    Platform.dispatch(UpdateDealForm({
                        form: {
                            SymbolId: eo.SymbolId,
                            SymbolCloseOnlyDateTime: Answer[1]?.SymbolCloseOnlyDateTime,
                            SymbolExpirationDate: Answer[1]?.SymbolExpirationDate,
                            Amount: eo.Amount,
                            OrderRate: eo.Rate,
                            PositionDirection: eo.PositionDirection,
                            PositionId: eo.Id,
                            RateRequestType: TradeType.EntryOrder,
                            StopLossValueRate: Utils.greaterThen0(eo.StopLossRate) ? eo.StopLossRate : null,
                            StopLossValueAmount: Utils.isNotNull(eo.StopLossAmount) ? eo.StopLossAmount : null,
                            StopLossChecked: Utils.greaterThen0(eo.StopLossRate) || dealEditTarget === DealEditTarget.StopLoss,
                            TakeProfitValueRate: Utils.greaterThen0(eo.TakeProfitRate) ? eo.TakeProfitRate : null,
                            TakeProfitValueAmount: Utils.isNotNull(eo.TakeProfitAmount) ? eo.TakeProfitAmount : null,
                            TakeProfitChecked: Utils.greaterThen0(eo.TakeProfitRate) || dealEditTarget === DealEditTarget.TakeProfit,
                            ExpirationChecked: Utils.greaterThen0(eo.ExpirationDate),
                            HasUserEnteredStopLossAmount: eo.HasUserEnteredStopLossAmount,
                            HasUserEnteredTakeProfitAmount: eo.HasUserEnteredTakeProfitAmount,
                            ExpirationDay,
                            ExpirationMonth,
                            ExpirationYear,
                        }
                    }));
                }
                if (payload.action) {
                    payload.action();
                } else {
                    if (WebUtil.isMobile()) {
                        Platform.dispatch(SetTradingModal({
                            tradingModalType: TradingModalType.CreateEditTrade,
                            info: {
                                visible: true
                            }
                        }));
                    } else {
                        Platform.dispatch(SetFilterVisible({visible: false}));
                        Platform.dispatch(ToggleMenu({collapse: true}));
                    }
                }
            }
        } else {
            this._logger.debug("Editing deal with ID: " + DealId + " already in progress");
        }
    }

    public doOpenDeal = async (payload: OpenDealPayload) => {
        const {deal, quotes} = Platform.reduxState<StoreState>();
        const form: DealForm = deal.form;
        if (form) {
            const accountState: AccountState = Platform.state(ServiceType.Account);
            const {RateRequestType, PositionDirection, SymbolId, Amount, OrderRate, StopLossChecked, StopLossValueRate, StopLossValueAmount, TakeProfitChecked, TakeProfitValueRate, TakeProfitValueAmount, UsePositionInsurance, UseSpreadCashBack, Comments} = form;
            const quote: Quote = quotes.quotes.get(SymbolId);
            const isMarketOrder: boolean = RateRequestType === TradeType.MarketOrder;
            this._logger.debug("Ask to open deal for symbol: " + SymbolId + " Market order: " + isMarketOrder);
            if (isMarketOrder) {
                const PositionPromotionTypes: PromotionType[] = [];
                if (UsePositionInsurance) {
                    PositionPromotionTypes.push(PromotionType.POSITION_INSURANCE);
                }
                if (UseSpreadCashBack) {
                    PositionPromotionTypes.push(PromotionType.SPREAD_CASHBACK);
                }
                const request: OpenDealRequest = {
                    SymbolId,
                    Direction: PositionDirection,
                    Rate: PositionDirection === TradeDirection.Sell  ? quote.Bid : quote.Ask,
                    Amount: Amount.toString(),
                    Comments,
                    ExpirationDate: null,
                    OwnerAccountId: accountState.accountId.toString(),
                    PositionPromotionTypes,
                    QuoteId: quote.Id,
                    StopLossAmount: StopLossChecked ? StopLossValueAmount : null,
                    StopLossRate: StopLossChecked ? StopLossValueRate : null,
                    TakeProfitAmount: TakeProfitChecked ? TakeProfitValueAmount : null,
                    TakeProfitRate: TakeProfitChecked ? TakeProfitValueRate : null,
                };
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "OpenPosition"));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this._logger.debug("Failed open position");
                    this.setRejectReason(answer[0]);
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    const response: OpenDealResponse = answer[1];
                    let openRate: number = 0;
                    if (response.P3 === 1) { // if true this is Buy and openRate is in property P33
                        openRate = response.P33;
                    } else { // else this is Sell and openRate is in property P32
                        openRate = response.P32;
                    }
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: TradingModalType.CreateEditTrade,
                        info: {
                            visible: false
                        }
                    }));
                    Platform.dispatch(AddInboxMessage({
                        message: {
                            type: InboxMessageType.TradePlaced,
                            parameters: [
                                Parameter.Of(InboxParameter.SymbolId, SymbolId),
                                Parameter.Of(InboxParameter.Direction, PositionDirection),
                                Parameter.Of(InboxParameter.Units, Amount),
                                Parameter.Of(InboxParameter.OrderRate, openRate),
                                Parameter.Of(InboxParameter.TradeType, RateRequestType)
                            ]
                        }
                    }));
                    if (payload.callback) {
                        payload.callback(response.P1);
                    }
                }
            } else {
                const {ExpirationChecked, ExpirationDay, ExpirationMonth, ExpirationYear} = form;
                let ExpirationDate: string = null;
                if (ExpirationChecked) {
                    ExpirationDate = ExpirationYear + "-" + Utils.pad(ExpirationMonth) + "-" + Utils.pad(ExpirationDay);
                }
                const request: BookEntryOrderRequest = {
                    SymbolId,
                    Amount: Amount.toString(),
                    Comments,
                    EntryOrderType: null,
                    ExpirationDate,
                    LimitOrderRate: OrderRate.toString(),
                    OwnerAccountId: accountState.accountId.toString(),
                    PositionDirection,
                    StopLossAmount: StopLossChecked ? StopLossValueAmount : null,
                    StopLossRate: StopLossChecked ? StopLossValueRate : null,
                    TakeProfitAmount: TakeProfitChecked ? TakeProfitValueAmount : null,
                    TakeProfitRate: TakeProfitChecked ? TakeProfitValueRate : null,
                };
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "BookEntryOrder"));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this._logger.debug("Failed book order");
                    this.setRejectReason(answer[0]);
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    const response: OpenDealResponse = answer[1];
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: TradingModalType.CreateEditTrade,
                        info: {
                            visible: false
                        }
                    }));
                    Platform.dispatch(AddInboxMessage({
                        message: {
                            type: InboxMessageType.OrderPlaced,
                            parameters: [
                                Parameter.Of(InboxParameter.SymbolId, SymbolId),
                                Parameter.Of(InboxParameter.Direction, PositionDirection),
                                Parameter.Of(InboxParameter.Units, Amount),
                                Parameter.Of(InboxParameter.OrderRate, OrderRate),
                                Parameter.Of(InboxParameter.TradeType, RateRequestType)
                            ]
                        }
                    }));
                    if (payload.callback) {
                        payload.callback(response.P1);
                    }
                }
            }
        } else {
            this._logger.debug("Can't open deal. Form absent");
        }
    }

    private setRejectReason = (error: HttpReject): void =>{
        const sError: string = XhrUtil.getRejectReason(error);
        Platform.dispatch(SetDealServerError({error: sError}));
        Platform.dispatch(ShowPopup({
            popup: {
                type: PopupType.ERROR,
                message: {
                    customValue: sError
                },
                showClose: true,
                icon: PopupIconType.ERROR,
                actions: [{type: PopupActionType.OK}]
            }
        }));
    }

    public doEditDeal = async (payload: EditDealPayload) => {
        const {deal, trades} = Platform.reduxState<StoreState>();
        const form: DealForm = deal.form;
        const {RateRequestType, PositionDirection, SymbolId, PositionId, Amount, OrderRate, StopLossChecked, StopLossValueRate, StopLossValueAmount,
            TakeProfitChecked, TakeProfitValueRate, TakeProfitValueAmount, HasUserEnteredStopLossAmount, HasUserEnteredTakeProfitAmount} = form;
        const isMarketOrder: boolean = RateRequestType === TradeType.MarketOrder;
        const {ExpirationChecked, ExpirationDay, ExpirationMonth, ExpirationYear} = form;
        let ExpirationDate: string = null;
        if (ExpirationChecked) {
            ExpirationDate = ExpirationYear + "-" + Utils.pad(ExpirationMonth) + "-" + Utils.pad(ExpirationDay);
        }
        this._logger.debug("Ask to edit deal: " + PositionId + " Market order: " + isMarketOrder);
        if (isMarketOrder) {
            const request: UpdatePositionRequest = {
                Comments: null,
                ExpirationDate: null,
                PositionId,
                StopLossAmount: StopLossChecked && HasUserEnteredStopLossAmount ? StopLossValueAmount : null,
                StopLossRate: StopLossChecked && !HasUserEnteredStopLossAmount ? StopLossValueRate : null,
                TakeProfitAmount: TakeProfitChecked && HasUserEnteredTakeProfitAmount ? TakeProfitValueAmount : null,
                TakeProfitRate: TakeProfitChecked && !HasUserEnteredTakeProfitAmount ? TakeProfitValueRate : null
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "UpdatePosition"));
            Platform.dispatch(HideLoader({}));
            Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
            if (answer[0]) {
                this._logger.debug("Failed edit position");
                this.setRejectReason(answer[0]);
            } else {
                const chartEngine: ChartEngine = Platform.engine(ServiceType.Chart);
                chartEngine.doUpdateDeal(PositionId, answer[1]);
                Platform.dispatch(SetDealServerError({error: null}));
                const openPosition: Deal = trades.OpenPositions.filter((d: Deal) => d.Id === PositionId)[0];
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.CreateEditTrade,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.TradeUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId),
                            Parameter.Of(InboxParameter.Direction, PositionDirection),
                            Parameter.Of(InboxParameter.Units, Amount),
                            Parameter.Of(InboxParameter.OrderRate, openPosition?.Rate)
                        ]
                    }
                }));
                if (payload.callback) {
                    payload.callback(answer[1].P1);
                }
            }
        } else {
            const request: UpdateEntryOrderRequest = {
                Amount,
                Comments: null,
                ExpirationDate,
                EntryOrderId: PositionId,
                EntryOrderType: null,
                LimitOrderRate: OrderRate,
                StopLossAmount: StopLossChecked && HasUserEnteredStopLossAmount ? StopLossValueAmount : null,
                StopLossRate: StopLossChecked && !HasUserEnteredStopLossAmount ? StopLossValueRate : null,
                TakeProfitAmount: TakeProfitChecked && HasUserEnteredTakeProfitAmount ? TakeProfitValueAmount : null,
                TakeProfitRate: TakeProfitChecked && !HasUserEnteredTakeProfitAmount ? TakeProfitValueRate : null
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "UpdateEntryOrder"));
            Platform.dispatch(HideLoader({}));
            Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
            if (answer[0]) {
                this._logger.debug("Failed update order");
                this.setRejectReason(answer[0]);
            } else {
                Platform.dispatch(SetDealServerError({error: null}));
                Platform.dispatch(SetTradingModal({
                    tradingModalType: TradingModalType.CreateEditTrade,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.OrderUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId),
                            Parameter.Of(InboxParameter.Direction, PositionDirection),
                            Parameter.Of(InboxParameter.Units, Amount),
                            Parameter.Of(InboxParameter.OrderRate, OrderRate)
                        ]
                    }
                }));
                if (payload.callback) {
                    payload.callback(answer[1].P1);
                }
            }
        }
    }

    public doEditDealFromChart = async (DealId: number, StopLoss: number, TakeProfit: number): Promise<OpenDealResponse> => {
        const {OpenPositions} = Platform.reduxState<StoreState>().trades;
        const deal: Deal = OpenPositions.filter((op: Deal) => op.Id === DealId)[0];
        if (deal) {
            this._logger.debug("Start to update deal from chart " + DealId + " SL: " + StopLoss + " TP " + TakeProfit);
            const request: UpdatePositionRequest = {
                Comments: null,
                ExpirationDate: null,
                PositionId: DealId,
                StopLossAmount: null,
                StopLossRate: StopLoss,
                TakeProfitAmount: null,
                TakeProfitRate: TakeProfit
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, OpenDealResponse] = await Utils.to(this.sendToTradeService(request, "UpdatePosition"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.debug("Failed edit deal from chart");
                XhrUtil.notifyReject(answer[0]);
            } else {
                this._logger.debug("Successful edit deal from chart");
                const {form} = Platform.reduxState<StoreState>().deal;
                if (form && (form.StopLossChecked || form.TakeProfitChecked)) {
                    const updatedDeal: Deal = TradeUtil.mapResponseToDeal(answer[1]);
                    Platform.dispatch(UpdateDealForm({
                        form: {
                            StopLossValueRate: updatedDeal.StopLossRate,
                            TakeProfitValueRate: updatedDeal.TakeProfitRate,
                            HasUserEnteredStopLossAmount: false,
                            HasUserEnteredTakeProfitAmount: false,
                        }
                    }));
                    const chartEngine: ChartEngine = Platform.engine(ServiceType.Chart);
                    chartEngine.doUpdateDeal(updatedDeal.Id, answer[1]);
                }
                Platform.dispatch(SetDealServerError({error: null}));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.TradeUpdated,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, deal.SymbolId),
                            Parameter.Of(InboxParameter.Direction, deal.PositionDirection),
                            Parameter.Of(InboxParameter.Units, deal.Amount),
                            Parameter.Of(InboxParameter.OrderRate, deal?.Rate)
                        ]
                    }
                }));
                return Promise.resolve(answer[1]);
            }
        } else {
            this._logger.debug("Can't update deal from chart. Deal not found: " + DealId);
        }
        return Promise.reject(false);
    }

    public doCloseDeals = async (payload: CloseDealsPayload) => {
        const {iDs, tradeType, modalType, callback} = payload;
        const {trades} = Platform.reduxState<StoreState>();
        if (Utils.isArrayNotEmpty(iDs)) {
            this._logger.debug("Try to close deals " + iDs);
            const notify = (deal: Deal, type: InboxMessageType): void => {
                Platform.dispatch(SetTradingModal({
                    tradingModalType: modalType,
                    info: {
                        visible: false
                    }
                }));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, deal.SymbolId),
                            Parameter.Of(InboxParameter.Direction, deal.PositionDirection),
                            Parameter.Of(InboxParameter.Units, deal.Amount),
                            Parameter.Of(InboxParameter.OrderRate, deal.Rate)
                        ]
                    }
                }));
            };
            const isMarketOrder: boolean = tradeType === TradeType.MarketOrder;
            if (iDs.length > 1) {
                Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                const deals: Deal[] = (isMarketOrder ? trades.OpenPositions : trades.EntryOrders).filter((trade: Deal) => iDs.indexOf(trade.Id) > -1);
                const request: CloseMultiplePositions = {
                    PositionIds: isMarketOrder ? iDs : null,
                    EntryOrderIds: isMarketOrder ? null : iDs
                };
                const path: string = isMarketOrder ? "CloseMultiplePositions" : "CancelMultipleEntryOrders";
                const answer: [HttpReject, CloseMultiplePositionsResponse] = await Utils.to(this.sendToTradeService(request, path));
                Platform.dispatch(HideLoader({}));
                Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                if (answer[0]) {
                    this.setRejectReason(answer[0]);
                } else {
                    Platform.dispatch(SetDealServerError({error: null}));
                    Platform.dispatch(SetTradingModal({
                        tradingModalType: modalType,
                        info: {
                            visible: false
                        }
                    }));
                    const response: CloseMultiplePositionsResponse = answer[1];
                    if (Utils.isArrayNotEmpty(response.PositionsThatClosedSuccessfully)) {
                        const {form} = Platform.reduxState<StoreState>().deal;
                        response.PositionsThatClosedSuccessfully.forEach((positionThatClosedSuccessfully: OpenDealResponse) => {
                            const id: number = positionThatClosedSuccessfully.P1;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                            }
                            const deal: Deal = deals.filter((d: Deal) => d.Id === id)[0];
                            if (deal) {
                                notify(deal, isMarketOrder ? InboxMessageType.TradeDeleted : InboxMessageType.OrderDeleted);
                            }
                        });
                    }
                    if (Utils.isArrayNotEmpty(response.PositionsThatFailedToClose)) {
                        const description: string[] = [Translations.text(TranslationKey.failedClosePositionDescription)];
                        response.PositionsThatFailedToClose.forEach((positionThatFailedToClose: PositionThatFailedToClose) => {
                            description.push(
                                Translations.text(TranslationKey.failedClosePosition,
                                    Parameter.Of(TranslationParam.name, positionThatFailedToClose.Item1),
                                    Parameter.Of(TranslationParam.value, positionThatFailedToClose.Item2))
                            );
                        });
                        Platform.dispatch(ShowPopup({
                            popup: {
                                type: PopupType.ERROR,
                                message: {
                                    customValue: description.join("<br />")
                                },
                                showClose: true,
                                icon: PopupIconType.ERROR,
                                actions: [{type: PopupActionType.OK}]
                            }
                        }));
                    }
                    if (callback) {
                        callback();
                    }
                }
            } else {
                if (isMarketOrder) {
                    const deal: Deal = trades.OpenPositions.filter((trade: Deal) => trade.Id === iDs[0])[0];
                    if (deal) {
                        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                        const request: ClosePosition = {
                            Comments: null,
                            PositionId: deal.Id,
                            QuoteId: deal.QuoteId,
                            Rate: deal.MarketRate
                        };
                        const answer: [HttpReject, BaseResponse] = await Utils.to(this.sendToTradeService(request, "ClosePosition"));
                        Platform.dispatch(HideLoader({}));
                        Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                        if (answer[0]) {
                            this.setRejectReason(answer[0]);
                        } else {
                            const {form} = Platform.reduxState<StoreState>().deal;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === deal.Id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                            }
                            Platform.dispatch(SetDealServerError({error: null}));
                            notify(deal, InboxMessageType.TradeDeleted);
                            if (callback) {
                                callback();
                            }
                        }
                    } else {
                        this._logger.debug("Can't find deal to close with id: " + iDs[0]);
                    }
                } else {
                    const order: Deal = trades.EntryOrders.filter((trade: Deal) => trade.Id === iDs[0])[0];
                    if (order) {
                        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
                        const request: CancelEntryOrder = {
                            Comments: null,
                            EntryOrderId: order.Id
                        };
                        const answer: [HttpReject, BaseResponse] = await Utils.to(this.sendToTradeService(request, "CancelEntryOrder"));
                        Platform.dispatch(HideLoader({}));
                        Platform.dispatch(SetDealFormInProgress({formInProgress: false}));
                        if (answer[0]) {
                            this.setRejectReason(answer[0]);
                        } else {
                            const {form} = Platform.reduxState<StoreState>().deal;
                            if (Utils.greaterThen0(form?.PositionId) && form?.PositionId === order.Id) {
                                Platform.dispatch(ToggleMenu({collapse: false}));
                            }
                            Platform.dispatch(SetDealServerError({error: null}));
                            notify(order, InboxMessageType.OrderDeleted);
                            if (callback) {
                                callback();
                            }
                        }
                    } else {
                        this._logger.debug("Can't find entry order to close with id: " + iDs[0]);
                    }
                }
            }
        } else {
            this._logger.debug("Can't close deals. Ids array empty");
        }
    }

    public doRollPositions = async ({SymbolId, symbol, nextSymbol}: RollPositionsPayload) => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: ClosePositionsByExposureRequest = {
            SymbolId,
            AccountId: accountState.accountId,
            Comments: null,
            IsRolling: true,
            PositionDirections: null
        };
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, ClosePositionsByExposureResponse] = await Utils.to(this.sendToTradeService(request, "ClosePositionsByExposure"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            this.setRejectReason(answer[0]);
        } else {
            const response: ClosePositionsByExposureResponse = answer[1];
            Platform.dispatch(ShowPopup({
                popup: {
                    type: PopupType.INFO,
                    message: {
                        trKey: response.RollingSuccess ? TranslationKey.tradeSymbolNotificationRollSuccess : TranslationKey.tradeSymbolNotificationRollFailed,
                        params: [
                            Parameter.Of(TranslationParam.name, symbol),
                            Parameter.Of(TranslationParam.value, nextSymbol)
                        ]
                    },
                    showClose: true,
                    icon: PopupIconType.INFO,
                    actions: [{type: PopupActionType.OK}]
                }
            }));
        }
    }

    private sendToWebProfitService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/WebProfitClientService.svc/json/" + path);
    }

    private sendToTradeService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "TradeServer/TradeService.svc/json/" + path);
    }

    private sendToAccountService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/" + path);
    }
}
